import { useMemo } from "react";
import { Link, Stack } from "@mui/material";
import { useEditor, useNode } from "@craftjs/core";
import { CssClass } from "@/components/editor/settings/ClassSettings";
import useResponsiveValues from "@/components/editor/hooks/useResponsiveValues";
import SocialSettings, { SocialMedia } from "../settings/SocialSettings";
import EmptyState from "@/components/EmptyState";
import SocialMediaIcon from "@/components/SocialMediaIcon/SocialMediaIcon";
import useVariableValues from "../hooks/useVariableValues";
import isEmpty from "lodash/isEmpty";

type Props = CssClass & SocialMedia;

const SocialMediaBlock = ({ cssClass, direction, profiles, spacing, labelPosition, variant, color, strokeWidth }: Partial<Props>) => {
  const { query } = useEditor();
  const {
    id,
    connectors: { connect, drag },
  } = useNode((state) => {
    return {
      hasSelectedNode: state.events.selected,
      hasDraggedNode: state.events.dragged,
    };
  });

  const isEditorEnabled = query.getOptions().enabled;
  const responsive = useResponsiveValues();
  const vv = useVariableValues(true);

  const computedProfiles = Object.values(profiles ?? {})
    .map((profile) => ({
      ...profile,
      alias: vv.getValue(profile.alias),
    }))
    .filter((profile) => !isEmpty(profile.alias));

  const labelPositionToFlexDirection = useMemo(() => {
    if (labelPosition === "left") return "row-reverse";
    if (labelPosition === "bottom") return "column";
    return "row";
  }, [labelPosition]);

  const rColor = responsive.getValue(color);
  const vColor = vv.getValue(rColor);

  const rStrokeWidth = useMemo(() => {
    return responsive.getValue(strokeWidth) ?? 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokeWidth]);

  return (
    <Stack key={id} ref={(ref: any) => connect(drag(ref))} className={`social-media ${responsive.getValue(cssClass)}`} spacing={spacing} direction={direction}>
      {!computedProfiles.length ? (
        <EmptyState title="Redes Sociales" description="Para ver tus perfiles aqui activalos desde la configuración" />
      ) : (
        computedProfiles.map((profile) =>
          isEditorEnabled ? (
            <Stack spacing={2} key={profile?.id} direction={labelPositionToFlexDirection}>
              <SocialMediaIcon icon={profile.id} variant={variant} color={vColor} strokeWidth={rStrokeWidth} className="social-profile__icon" />
              {labelPosition !== "hidden" ? <span style={{ color: "black" }}>{profile?.label}</span> : null}
            </Stack>
          ) : (
            <Link key={profile.id} href={isEditorEnabled ? "#" : profile.url + profile.alias} className="social-profile" target="_blank">
              <Stack spacing={2} direction={labelPositionToFlexDirection}>
                <SocialMediaIcon icon={profile.id} variant={variant} color={vColor} strokeWidth={rStrokeWidth} className="social-profile__icon" />
                {labelPosition !== "hidden" ? <span style={{ color: "black" }}>{profile?.label}</span> : null}
              </Stack>
            </Link>
          )
        )
      )}
    </Stack>
  );
};

SocialMediaBlock.craft = {
  displayName: "Redes Sociales",
  related: {
    settings: SocialSettings,
  },
};

export default SocialMediaBlock;
